import { motion } from "framer-motion";
import { ROADMAP } from "@/config/kingbullish";
import { useUnlock } from "@/context/TokenDataContext";
import { formatCompactUsd, formatProgress } from "@/lib/format";
import { RoadmapMilestone } from "@/components/RoadmapMilestone";
import { LaunchBadge } from "@/components/LaunchBadge";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/motion/Reveal";

const ProgressToUnlock = () => {
  const { marketCap, progress, unlockLabel, isUnlocked, isPreLaunch } = useUnlock();
  return (
    <Reveal delay={0.1} className="mb-10 lg:mb-14">
      <div className="glass hud rounded-xl px-5 py-4 sm:px-6" style={{ "--hud-color": isUnlocked ? "#00FF66" : "#FFD700" }} data-testid="roadmap-progress" data-prelaunch={isPreLaunch ? "true" : "false"}>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-8">
          <div className="shrink-0 leading-none">
            <div className="flex flex-wrap items-center gap-2">
              <span className="eyebrow">PROGRESS TO {unlockLabel} UNLOCK</span>
              {isPreLaunch && <LaunchBadge testId="roadmap-progress-status" />}
            </div>
            <div className="mt-2 font-mono text-xl font-bold text-white">
              <span data-testid="roadmap-progress-marketcap">{formatCompactUsd(marketCap)}</span>
              <span className="text-white/35"> / {unlockLabel}</span>
            </div>
          </div>
          <div className="relative h-3 flex-1 overflow-hidden rounded-full bg-white/[0.06] ring-1 ring-white/10">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-neon via-cyan2 to-gold shadow-[0_0_18px_rgba(0,255,102,0.6)]"
              initial={{ width: 0 }}
              animate={{ width: `${progress * 100}%` }}
              transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
          <div className={`shrink-0 font-display text-lg font-bold ${isUnlocked ? "text-neon glow-green" : "text-gold glow-gold"}`} data-testid="roadmap-progress-percent">
            {formatProgress(progress)}
          </div>
        </div>
      </div>
    </Reveal>
  );
};

export const Roadmap = () => {
  const { marketCap, isUnlocked, isPreLaunch } = useUnlock();
  // Pre-launch: nothing is "in progress" — every milestone waits for launch.
  const firstUnreached = marketCap === null || isPreLaunch ? null : ROADMAP.find((m) => marketCap < m.cap);

  const stateOf = (m) => {
    if (m.major) return isUnlocked ? "unlocked" : "locked";
    if (marketCap !== null && marketCap >= m.cap) return "reached";
    return firstUnreached?.id === m.id ? "active" : "locked";
  };

  return (
    <section id="roadmap" className="section relative overflow-hidden" data-testid="roadmap-section">
      <div className="planet -right-32 top-10 h-[420px] w-[420px] opacity-70 lg:-right-20" />
      <div className="container-x relative">
        <SectionHeading
          title="ROADMAP"
          eyebrow="FROM A TOKEN TO A GLOBAL ECOSYSTEM"
          testId="roadmap-heading"
          right={
            <blockquote className="font-display text-base font-semibold tracking-wide text-white/80 md:text-lg lg:text-right">
              “MORE THAN A TOKEN.<br className="hidden lg:block" /> A MOVEMENT.”
            </blockquote>
          }
        />
        <ProgressToUnlock />
        <div className="mb-3 hidden items-center gap-2 font-mono text-[0.62rem] tracking-widest text-white/40 md:flex xl:hidden" data-testid="roadmap-scroll-hint">
          <span className="h-px w-8 bg-white/20" /> SCROLL HORIZONTALLY TO EXPLORE ALL MILESTONES <span className="h-px w-8 bg-white/20" />
        </div>
        <div className="rm-track" data-testid="roadmap-track">
          {ROADMAP.map((m, i) => (
            <RoadmapMilestone key={m.id} milestone={m} state={stateOf(m)} index={i} ready={marketCap !== null} />
          ))}
        </div>
      </div>
    </section>
  );
};
